import { customerLabel } from '../lib/customerName';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { Meteor } from 'meteor/meteor';
import i18n from '../../../i18n';
import { ApiError } from '../../../lib/api';
import {
  MARKETING_SOURCE_KINDS,
  type MarketingDiscoveredResource,
  type MarketingOutletOption,
} from '../data/marketingTypes';
import type { ConnectedSourceId } from '../types';
import {
  mappingCounts,
  reviewMappedOutlets,
  searchOutletsToAdd,
  suggestedOutletsToAdd,
  type MappingListFilter,
} from '../lib/mappingOutlets';
import { useDialogFocus } from '../lib/useDialogFocus';
import { DiscoveryPicker } from './growth/DiscoveryPicker';
import { ConnectPropertySheet, ConnectToast, type ConnectedToast } from './growth/ConnectPropertySheet';
import {
  fetchRestaurantOptions,
  type ConnectRequest,
  type RestaurantOption,
} from '../data/googleAccounts';
import { Icon } from './shared/Icon';
import { Infotip } from './shared/Infotip';
import { fmtInt } from '../lib/growthFormat';

/** Wizard copy, in the person's language (WP4.1). */
function w(key: string, options?: Record<string, unknown>): string {
  return i18n.t(`growth:wizard.${key}`, options);
}

/** Fixed reason codes from the backend callback (OAUTH_FAILURE_REASONS). */
const OAUTH_REASON_KEY: Record<string, string> = {
  access_denied: 'growth:oauth.accessDenied',
  missing_code: 'growth:oauth.missingCode',
  not_signed_in: 'growth:oauth.notSignedIn',
  state_invalid: 'growth:oauth.stateInvalid',
  wrong_user: 'growth:oauth.wrongUser',
  session_mismatch: 'growth:oauth.sessionMismatch',
  no_access: 'growth:oauth.noAccess',
  exchange_failed: 'growth:oauth.exchangeFailed',
  identity_unverified: 'growth:oauth.identityUnverified',
  consent_required: 'growth:oauth.consentRequired',
};

const PRODUCT_LABEL_KEY: Record<string, string> = {
  ga4: 'growth:setup.analytics',
  ads: 'growth:setup.ads',
  gsc: 'growth:setup.gsc',
  gbp: 'growth:setup.gbp',
};

function oauthReasonMessage(raw: string | null | undefined): string {
  const n = (raw || '').toLowerCase().replace(/[\s-]+/g, '_');
  const key = OAUTH_REASON_KEY[n];
  return key ? i18n.t(key) : i18n.t('growth:oauth.didNotFinishRetry');
}

type Step = 'start' | 'discover' | 'map' | 'scope' | 'utm' | 'done';

function kindLabel(kind: string): string {
  return w(`kind.${kind}`);
}

type EventKey = 'widgetView' | 'bookingStarted' | 'bookingCompleted';

const EVENT_KEYS: readonly EventKey[] = [
  'widgetView',
  'bookingStarted',
  'bookingCompleted',
];

function eventLabel(key: EventKey): string {
  return w(`event.${key}`);
}

type DetectedEvent = { name: string; count: number };

const CUSTOM_EVENT_OPTION = '\u0000custom';
// The configured name, kept selectable even though the property never fired it.
const UNRECORDED_EVENT_OPTION = '\u0000unrecorded';

const GTM_GUIDE =
  'https://knowledge.aleno.me/en/kb/track-conversions-using-google-tag-manager-gtm';

type AdsCampaignOption = { campaignId: string; campaignName: string };

type ConnectionStatus = {
  enabled: boolean;
  links: Array<{ linkId: string; kind?: string; status?: string }>;
  auths?: Array<{
    authId: string;
    kind?: string;
    status?: string;
    isOwner?: boolean;
    sessionLive?: boolean;
    refreshable?: boolean;
    tokenExpiresAt?: number;
  }>;
  resources: Array<{
    resourceId: string;
    linkId?: string;
    authId?: string;
    externalId: string;
    displayName: string;
    kind: string;
    restaurantIds?: string[];
    syncStatus?: string;
    campaignCatalog?: AdsCampaignOption[];
  }>;
  adsCampaigns?: AdsCampaignOption[];
  campaignUtmMap?: Record<string, string>;
  outlets?: MarketingOutletOption[];
  /** This customer's restaurants: a property maps to these only (B7). */
  orgOutletIds?: string[];
  viewerIsStaff?: boolean;
  /** False while only aleno staff may start a Google connection. */
  canConnect?: boolean;
  /** The customer's name (its restaurant group), for "Belongs here". */
  customerName?: string | null;
  message?: string;
};

function authCoversKind(authKind: string | undefined, kind: string): boolean {
  return !authKind || authKind === kind || authKind === 'google';
}

function isLiveAuth(
  auth: {
    kind?: string;
    sessionLive?: boolean;
    refreshable?: boolean;
    isOwner?: boolean;
    tokenExpiresAt?: number;
  },
  kind: string,
): boolean {
  if (!authCoversKind(auth.kind, kind)) return false;
  if (auth.isOwner === false) return false;
  if (auth.refreshable) return true;
  if (!auth.sessionLive) return false;
  if (auth.tokenExpiresAt && auth.tokenExpiresAt <= Date.now()) return false;
  return true;
}

function sessionLine(
  auths: Array<{
    authId: string;
    kind?: string;
    sessionLive?: boolean;
    refreshable?: boolean;
    isOwner?: boolean;
    tokenExpiresAt?: number;
  }>,
  kind: string,
): string | null {
  const live = auths.filter(a => isLiveAuth(a, kind));
  if (!live.length) return null;
  const best = live.reduce((a, b) =>
    (a.tokenExpiresAt || 0) >= (b.tokenExpiresAt || 0) ? a : b,
  );
  if (best.refreshable && !best.sessionLive) {
    return w('sessionRefreshes');
  }
  const remain = best.tokenExpiresAt
    ? Math.max(0, Math.round((best.tokenExpiresAt - Date.now()) / 60000))
    : null;
  return remain != null ? w('sessionMinutes', { count: remain }) : w('sessionLive');
}

export function MarketingConnectWizard({
  sourceId,
  restaurantId,
  onClose,
  onConnected,
  onMappedRestaurants,
  embedded = false,
  scopedRestaurantIds = [],
  intent = null,
}: {
  sourceId: ConnectedSourceId;
  restaurantId: string;
  onClose: () => void;
  onConnected: () => void;
  onMappedRestaurants?: (restaurantIds: string[]) => void;
  embedded?: boolean;
  scopedRestaurantIds?: string[];
  intent?: 'reconnect' | null;
}) {
  const kind = MARKETING_SOURCE_KINDS[sourceId] ?? 'ga4';
  // As a drawer it is a modal dialog: focus inside, Escape closes (WP4.7).
  const drawerRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogFocus(drawerRef, onClose, !embedded);
  const [step, setStep] = useState<Step>('start');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<ConnectionStatus | null>(null);
  const [statusReady, setStatusReady] = useState(false);
  const [authId, setAuthId] = useState<string | null>(null);
  /** The connection's name, shown to colleagues instead of the Google account (filtering audit 7.1, rule 6). */
  const [connectionNameDraft, setConnectionNameDraft] = useState('');
  const [mode, setMode] = useState<string>('oauth');
  const [discovered, setDiscovered] = useState<MarketingDiscoveredResource[]>([]);
  const [outlets, setOutlets] = useState<MarketingOutletOption[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [mapping, setMapping] = useState<Record<string, string[]>>({});
  const [utmMap, setUtmMap] = useState<Record<string, string>>({});
  const [adsCampaigns, setAdsCampaigns] = useState<AdsCampaignOption[]>([]);
  const [eventConfig, setEventConfig] = useState({
    widgetView: 'view_reservation_widget',
    bookingStarted: 'begin_reservation',
    bookingCompleted: 'purchase',
  });
  const [detectedEvents, setDetectedEvents] = useState<DetectedEvent[] | null>(null);
  const [eventsBusy, setEventsBusy] = useState(false);
  const [customEvent, setCustomEvent] = useState<Record<EventKey, boolean>>({
    widgetView: false,
    bookingStarted: false,
    bookingCompleted: false,
  });
  const [outletQueryByResource, setOutletQueryByResource] = useState<
    Record<string, string>
  >({});
  const [listFilter, setListFilter] = useState<MappingListFilter>('selected');
  const [pendingMappedIds, setPendingMappedIds] = useState<string[]>([]);
  const [syncNotice, setSyncNotice] = useState<string | null>(null);
  const [orgOutletIds, setOrgOutletIds] = useState<string[] | null>(null);
  const [viewerIsStaff, setViewerIsStaff] = useState(false);
  /** The Google account just signed in with, shown once and never stored. */
  const [accountEmail, setAccountEmail] = useState<string | null>(null);
  /** Products the person unticked on Google's screen at the last sign-in. */
  const [notGranted, setNotGranted] = useState<string[]>([]);
  /** When the property list was fetched from Google (cached up to an hour). */
  const [discoveredAt, setDiscoveredAt] = useState<number | null>(null);
  /** Staff: properties another customer already uses, awaiting confirmation. */
  /** What Google could not list, and why (WP1.10). */
  const [discoveryWarnings, setDiscoveryWarnings] = useState<
    Array<{ kind: string; status: string; accountId?: string }>
  >([]);
  const oauthPopupRef = useRef<Window | null>(null);
  /**
   * The Google account just signed in with: its properties can feed any
   * restaurant the person can open, through the confirmation sheet
   * (docs/19 section 5).
   */
  const [credentialId, setCredentialId] = useState<string | null>(null);
  const [connectSheet, setConnectSheet] = useState<ConnectRequest | null>(null);
  const [connectToast, setConnectToast] = useState<ConnectedToast | null>(null);
  const [restaurantOptions, setRestaurantOptions] = useState<RestaurantOption[]>([]);
  const openConnect = (item: MarketingDiscoveredResource, initialRestaurantId: string | null) => {
    if (!credentialId) return;
    setConnectSheet({
      credentialId,
      property: { kind: item.kind, externalId: item.externalId, displayName: item.displayName },
      initialRestaurantId,
      suggestions: (item.elsewhereSuggestions || []).map(s => ({ id: s.id, name: s.name, city: s.city })),
    });
    if (!restaurantOptions.length) {
      void fetchRestaurantOptions()
        .then(setRestaurantOptions)
        .catch(() => undefined);
    }
  };

  const existingForKind = useMemo(
    () => (status?.resources || []).filter(r => r.kind === kind),
    [status, kind],
  );

  const authsForKind = useMemo(
    () => (status?.auths || []).filter(a => authCoversKind(a.kind, kind)),
    [status, kind],
  );
  const liveAuths = useMemo(
    () => authsForKind.filter(a => isLiveAuth(a, kind)),
    [authsForKind, kind],
  );
  const liveAuth = liveAuths[0] || null;
  /**
   * A working connection someone else made (for example aleno staff) powers
   * this org's data for everyone; it is not a reason to "reconnect".
   */
  const orgConnectionLive = useMemo(
    () =>
      authsForKind.some(
        a =>
          a.isOwner === false &&
          a.status !== 'reauth_required' &&
          a.status !== 'revoked' &&
          isLiveAuth({ ...a, isOwner: true }, kind),
      ),
    [authsForKind, kind],
  );
  const mappedAuthIds = useMemo(
    () => [
      ...new Set(
        existingForKind
          .map(resource => resource.authId || resource.linkId || '')
          .filter(Boolean),
      ),
    ],
    [existingForKind],
  );
  const reviewableMappingCount = useMemo(
    () => existingForKind.filter(resource => resource.authId || resource.linkId).length,
    [existingForKind],
  );

  /**
   * The source tile's Reconnect button is an instruction, not a suggestion.
   * A leftover live lease from an earlier attempt must not hide that verb
   * and dump the operator on Manage mappings.
   */
  const showReconnect = intent === 'reconnect' || (!liveAuth && !orgConnectionLive && (
    existingForKind.length > 0 ||
    (status?.auths || []).some(
      a =>
        authCoversKind(a.kind, kind) &&
        !a.refreshable &&
        (!a.sessionLive || a.status === 'reauth_required'),
    ) ||
    (status?.resources || []).some(r => r.kind === kind && r.syncStatus === 'reauth_required')
  ));
  const liveSession = sessionLine(status?.auths || [], kind);
  const connectBlocked = status?.canConnect === false;

  const kindDiscovered = useMemo(
    () => discovered.filter(d => !d.kind || d.kind === kind),
    [discovered, kind],
  );

  const selectedList = useMemo(
    () => kindDiscovered.filter(d => selected[d.externalId]),
    [kindDiscovered, selected],
  );

  const scopedSet = useMemo(
    () => new Set(scopedRestaurantIds),
    [scopedRestaurantIds],
  );
  const autoLoadedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setStatusReady(false);
    autoLoadedRef.current = false;
    (async () => {
      try {
        const s = await Meteor.callPromise<ConnectionStatus>(
          'opsCommand/getMarketingConnectionStatus',
          restaurantId,
          null,
        );
        if (cancelled) return;
        setStatus(s);
        if (s.outlets?.length) setOutlets(s.outlets);
        if (s.orgOutletIds) setOrgOutletIds(s.orgOutletIds);
        setViewerIsStaff(Boolean(s.viewerIsStaff));
        if (s.campaignUtmMap) setUtmMap({ ...s.campaignUtmMap });
        if (s.adsCampaigns?.length) setAdsCampaigns(s.adsCampaigns);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) setStatusReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [restaurantId]);

  const loadDiscovery = async (authId: string, force = false) => {
    setBusy(true);
    setError(null);
    try {
      const completed = await Meteor.callPromise<{
        link: { authId?: string; linkId?: string };
        auth?: { name?: string | null; credentialId?: string | null } | null;
        discovered: MarketingDiscoveredResource[];
        outlets: MarketingOutletOption[];
        orgOutletIds?: string[];
        viewerIsStaff?: boolean;
        confirmAccount?: { email?: string } | null;
        notGrantedProducts?: string[];
        warnings?: Array<{ kind: string; status: string; accountId?: string }>;
        discoveredAt?: number | null;
        mode: string;
      }>(
        'opsCommand/getMarketingDiscovery',
        restaurantId,
        authId,
        force,
        // showAllRestaurants: retired; only this customer's restaurants can
        // be mapped (filtering audit B7).
        false,
      );
      if (completed.orgOutletIds) setOrgOutletIds(completed.orgOutletIds);
      if (completed.viewerIsStaff != null) setViewerIsStaff(completed.viewerIsStaff);
      if (completed.confirmAccount?.email) setAccountEmail(completed.confirmAccount.email);
      setConnectionNameDraft(completed.auth?.name || '');
      setCredentialId(
        completed.auth?.credentialId || (authId.startsWith('cred_') ? authId : null),
      );
      setNotGranted(completed.notGrantedProducts || []);
      setDiscoveredAt(completed.discoveredAt ?? null);
      setDiscoveryWarnings(
        (completed.warnings || []).filter(item => item && typeof item === 'object'),
      );
      applyCompleted(completed);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStep('start');
    } finally {
      setBusy(false);
    }
  };

  const finishReconnect = async (newAuthId: string) => {
    setBusy(true);
    setError(null);
    try {
      // Growth fetches Google live (WP2.3): nothing to queue. Only when no
      // mapped property can be fetched any more does the mapping flow open.
      const status = await Meteor.callPromise<{ liveMappedResources?: number }>(
        'opsCommand/getMarketingConnectionStatus',
        restaurantId,
        null,
      );
      if (!status?.liveMappedResources) {
        await loadDiscovery(newAuthId, true);
        return;
      }
      setAuthId(newAuthId);
      setSyncNotice(w('reconnected'));
      setStep('done');
      onConnected();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStep('start');
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (!data || data.type !== 'aleno-google-oauth') return;
      if (data.ok && data.authId) {
        oauthPopupRef.current = null;
        if (intent === 'reconnect') {
          void finishReconnect(String(data.authId));
        } else {
          void loadDiscovery(String(data.authId));
        }
        return;
      }
      oauthPopupRef.current = null;
      setBusy(false);
      setError(oauthReasonMessage(data.reason));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [restaurantId, intent, kind]);

  useEffect(() => {
    if (!busy || !oauthPopupRef.current) return;
    const timer = window.setInterval(() => {
      if (!oauthPopupRef.current?.closed) return;
      oauthPopupRef.current = null;
      setBusy(false);
    }, 500);
    return () => window.clearInterval(timer);
  }, [busy]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectAuthId = params.get('authId');
    const oauth = params.get('oauth');
    if (oauth === 'ok' && redirectAuthId) {
      if (intent === 'reconnect') {
        void finishReconnect(redirectAuthId);
      } else {
        void loadDiscovery(redirectAuthId);
      }
    } else if (oauth === 'error') {
      setError(oauthReasonMessage(params.get('reason')));
    }
    if (oauth || redirectAuthId || params.has('reason')) {
      params.delete('oauth');
      params.delete('authId');
      params.delete('reason');
      const query = params.toString();
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
      );
    }
  }, [restaurantId, intent, kind]);

  useEffect(() => {
    if (step !== 'start' || !liveAuth || mappedAuthIds.length) return;
    if (autoLoadedRef.current) return;
    autoLoadedRef.current = true;
    void loadDiscovery(liveAuth.authId);
  }, [liveAuth?.authId, mappedAuthIds.length, step]);

  const waitingForDiscovery =
    statusReady &&
    step === 'start' &&
    Boolean(liveAuth) &&
    mappedAuthIds.length === 0 &&
    intent !== 'reconnect' &&
    (busy || !autoLoadedRef.current);
  const sessionCheckPending = !statusReady || waitingForDiscovery;

  const selectedPropertyIds = useMemo(
    () => selectedList.map(d => d.externalId).sort().join('|'),
    [selectedList],
  );

  /** Configured events the property has never actually recorded. */
  const missingEvents = useMemo(() => {
    if (!detectedEvents?.length) return [];
    const seen = new Set(detectedEvents.map(e => e.name));
    return EVENT_KEYS.filter(k => eventConfig[k] && !seen.has(eventConfig[k]));
  }, [detectedEvents, eventConfig]);

  /**
   * Ask the property which events it actually records, so the pickers offer
   * real names. Only on the mapping step, where the properties are known.
   */
  useEffect(() => {
    if (kind !== 'ga4' || step !== 'map' || !authId || !selectedPropertyIds) return;
    let cancelled = false;
    setEventsBusy(true);
    (async () => {
      try {
        const res = await Meteor.callPromise<{ events: DetectedEvent[] }>(
          'opsCommand/getGa4EventNames',
          restaurantId,
          authId,
          selectedPropertyIds.split('|'),
        );
        if (!cancelled) setDetectedEvents(res.events || []);
      } catch {
        // A failed listing is not a blocker: the fields stay free text.
        if (!cancelled) setDetectedEvents(null);
      } finally {
        if (!cancelled) setEventsBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, step, authId, selectedPropertyIds, restaurantId]);

  /** After a connect or its undo: the rows' state from the server, keeping
   * what the person ticked and mapped in this wizard. */
  const refreshDiscovered = async () => {
    if (!authId) return;
    try {
      const completed = await Meteor.callPromise<{ discovered: MarketingDiscoveredResource[] }>(
        'opsCommand/getMarketingDiscovery',
        restaurantId,
        authId,
        false,
        false,
      );
      setDiscovered(completed.discovered || []);
    } catch {
      // The connect itself succeeded; the rows catch up on the next load.
    }
  };

  const applyCompleted = (completed: {
    link: { authId?: string; linkId?: string };
    discovered: MarketingDiscoveredResource[];
    outlets: MarketingOutletOption[];
    mode: string;
  }) => {
    setAuthId(completed.link.authId || completed.link.linkId || null);
    setMode(completed.mode);
    setDiscovered(completed.discovered || []);
    setOutlets(completed.outlets || []);
    const sel: Record<string, boolean> = {};
    const map: Record<string, string[]> = {};
    const found = completed.discovered || [];
    const forKind = found.filter(d => !d.kind || d.kind === kind);
    for (const d of found) {
      // Suggestions stay on the resource as hints. Ticking them here is what
      // mapped every restaurant that shared a city with the property name.
      // With the server's groups nothing is ticked for the person, not even
      // a lone property (it may well be another customer's).
      sel[d.externalId] = !d.group && forKind.length === 1 && (!d.kind || d.kind === kind);
      map[d.externalId] = [];
    }
    setSelected(sel);
    setMapping(map);
    if (found.length) {
      setStep('discover');
      return;
    }
    setStep('start');
    setError(w('noPropertiesReturned'));
  };

  const loadExistingMappings = (requestedAuthId?: string) => {
    const selectedAuthId = requestedAuthId || mappedAuthIds[0];
    const resources = selectedAuthId
      ? existingForKind.filter(
          resource => (resource.authId || resource.linkId) === selectedAuthId,
        )
      : [];
    if (!resources.length || !selectedAuthId) {
      setError(w('noSavedMappings'));
      return;
    }
    setAuthId(selectedAuthId);
    setMode('manage');
    const asDiscovered: MarketingDiscoveredResource[] = resources.map(r => ({
      externalId: r.externalId,
      displayName: r.displayName,
      kind: r.kind as 'ga4' | 'ads' | 'gsc' | 'gbp',
      suggestedRestaurantIds: r.restaurantIds || [],
    }));
    setDiscovered(asDiscovered);
    const sel: Record<string, boolean> = {};
    const map: Record<string, string[]> = {};
    for (const r of resources) {
      sel[r.externalId] = true;
      map[r.externalId] = [...(r.restaurantIds || [])];
    }
    setSelected(sel);
    setMapping(map);
    setOutlets(status?.outlets || outlets);
    setStep('map');
  };

  const startConnect = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    let waitingForPopup = false;
    try {
      if (status && status.enabled === false) {
        setError(i18n.t('growth:setup.disabled'));
        return;
      }
      // Only this product is asked for; Google keeps what the account
      // granted before (include_granted_scopes).
      const started = await Meteor.callPromise<{
        mode: string;
        state: string;
        authUrl?: string | null;
        message?: string;
      }>('opsCommand/startGoogleOAuth', restaurantId, 'google', [kind]);

      setMode(started.mode || 'oauth');

      if (!started.authUrl) {
        setError(i18n.t('growth:oauth.noAuthUrl'));
        return;
      }
      const popup = window.open(
        started.authUrl,
        'aleno-google-oauth',
        'width=520,height=740',
      );
      if (!popup) {
        setError(i18n.t('growth:oauth.popupBlocked'));
        return;
      }
      oauthPopupRef.current = popup;
      waitingForPopup = true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!waitingForPopup) setBusy(false);
    }
  };

  const save = async () => {
    if (!authId) return;
    setBusy(true);
    setError(null);
    try {
      const selections = kindDiscovered.map(d => ({
        externalId: d.externalId,
        displayName: d.displayName,
        kind: d.kind,
        selected: !!selected[d.externalId],
        restaurantIds: mapping[d.externalId] || [],
      }));
      const mappedCount = selections.filter(
        s => s.selected && s.restaurantIds.length > 0,
      ).length;
      if (mappedCount === 0) {
        setError(w('mapAtLeastOne'));
        setBusy(false);
        return;
      }
      if (connectionNameDraft.includes('@')) {
        // The name is shown instead of the Google account (rule 6).
        setError(i18n.t('growth:connections.renameNoEmail'));
        setBusy(false);
        return;
      }
      if (orgOutletIds) {
        // A property maps to one customer's restaurants only, for staff too
        // (filtering audit B7); the server refuses the rest as well.
        const own = new Set(orgOutletIds);
        const foreign = [
          ...new Set(
            selections.flatMap(s => (s.selected ? s.restaurantIds : [])),
          ),
        ].filter(id => !own.has(id));
        if (foreign.length) {
          setError(i18n.t('growth:mapping.crossOrgBlocked'));
          setBusy(false);
          return;
        }
      }
      // Rule 3: a property that already feeds another customer is refused,
      // for staff too; there is no override (the server refuses it as well).
      const feedsOther = kindDiscovered.filter(
        d =>
          d.mappedElsewhere &&
          selected[d.externalId] &&
          (mapping[d.externalId] || []).length > 0,
      );
      if (feedsOther.length) {
        setError(
          i18n.t('growth:mapping.feedsOtherBlocked', {
            names: feedsOther.map(d => d.displayName || d.externalId).join(', '),
          }),
        );
        setBusy(false);
        return;
      }
      const result = await Meteor.callPromise<{
        ok: boolean;
        campaigns?: AdsCampaignOption[];
        status?: string;
      }>(
        'opsCommand/saveMarketingMappings',
        restaurantId,
        authId,
        // The venues chosen here, wherever the wizard runs (Growth included).
        selections,
        kind === 'ga4' ? eventConfig : null,
        null,
        // deferMapping: retired; a save always maps the chosen venues.
        false,
        // allowCrossOrg: retired; nobody maps across customers (B7).
        false,
        // allowSharedProperty: retired; a property feeds one customer only.
        false,
        // The connection's name (rule 6); empty clears it.
        connectionNameDraft.trim(),
      );
      setSyncNotice(w('mappingsSaved'));
      const mappedIds = [
        ...new Set(
          selections.flatMap(s => (s.selected ? s.restaurantIds : [])),
        ),
      ];
      const newlyMapped = mappedIds.filter(id => !scopedSet.has(id));
      if (newlyMapped.length) {
        setPendingMappedIds(mappedIds);
        setStep('scope');
      } else if (kind === 'ads') {
        if (result.campaigns?.length) setAdsCampaigns(result.campaigns);
        setStep('utm');
      } else {
        setStep('done');
        onConnected();
      }
    } catch (e) {
      if (e instanceof ApiError && e.code === 'cross_org_mapping') {
        // The server knows the org of every restaurant and refuses the rest.
        setError(i18n.t('growth:mapping.crossOrgBlocked'));
      } else if (
        e instanceof ApiError &&
        (e.code === 'feeds_other_customer' || e.code === 'mapped_elsewhere')
      ) {
        setError(i18n.t('growth:mapping.feedsOtherBlockedUnknown'));
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const saveUtm = async () => {
    setBusy(true);
    setError(null);
    try {
      const cleaned: Record<string, string> = {};
      for (const [k, v] of Object.entries(utmMap)) {
        const t = (v || '').trim();
        if (t) cleaned[k] = t;
      }
      await Meteor.callPromise(
        'opsCommand/saveCampaignUtmMap',
        restaurantId,
        cleaned,
      );
      setStep('done');
      onConnected();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const continueAfterScope = (applyScope: boolean) => {
    if (applyScope && pendingMappedIds.length) {
      onMappedRestaurants?.(pendingMappedIds);
    }
    setPendingMappedIds([]);
    if (kind === 'ads') {
      setStep('utm');
    } else {
      setStep('done');
      onConnected();
    }
  };

  const mappedPreviewIds = useMemo(
    () => [
      ...new Set(
        selectedList.flatMap(d => mapping[d.externalId] || []),
      ),
    ],
    [selectedList, mapping],
  );
  const mappedPreviewNew = mappedPreviewIds.filter(id => !scopedSet.has(id));

  const setResourceMapping = (resourceId: string, nextIds: string[]) => {
    setMapping(m => ({ ...m, [resourceId]: [...new Set(nextIds)] }));
  };

  const addOutlets = (resourceId: string, ids: string[]) => {
    setMapping(m => ({
      ...m,
      [resourceId]: [...new Set([...(m[resourceId] || []), ...ids])],
    }));
  };

  const removeOutlet = (resourceId: string, outletId: string) => {
    setMapping(m => ({
      ...m,
      [resourceId]: (m[resourceId] || []).filter(id => id !== outletId),
    }));
  };

  const body = (
    <>
        <div className={embedded ? 'mkt-wizard-head' : 'dr-head'}>
          <div>
            <h2 id={titleId}>
              {embedded
                ? w('chooseProperties', { product: kindLabel(kind) })
                : showReconnect
                  ? w('reconnectProduct', { product: kindLabel(kind) })
                  : w('connectProduct', { product: kindLabel(kind) })}
            </h2>
            <p>{showReconnect && !embedded ? w('reconnectIntro') : w('intro')}</p>
          </div>
          {!embedded && (
            <button type="button" className="ghost-btn" onClick={onClose} aria-label={w('close')}>
              <Icon name="close" size={18} />
            </button>
          )}
        </div>
        <div className={embedded ? 'mkt-wizard-scroll' : 'dr-body'}>
          {error && <div className="mkt-wizard-error">{error}</div>}

          {step === 'start' && (
            <div className="mkt-wizard-step" aria-busy={sessionCheckPending}>
              {!sessionCheckPending && connectBlocked ? (
                <p className="mkt-wizard-meta" role="note">
                  <strong>{i18n.t('growth:setup.askAleno')}</strong>{' '}
                  {i18n.t('growth:setup.askAlenoDetail')}
                </p>
              ) : null}
              {sessionCheckPending ? (
                <div className="mkt-wizard-checking" role="status" aria-live="polite">
                  <Icon
                    name="refresh"
                    size={16}
                    className="mkt-wizard-checking-icon"
                  />
                  <p>{!statusReady ? w('checking') : w('loadingProperties')}</p>
                </div>
              ) : null}
              {!sessionCheckPending && status && status.enabled === false && (
                <div className="mkt-wizard-error">
                  <Trans i18nKey="growth:wizard.marketingOff" components={{ code: <code /> }} />
                </div>
              )}
              {!sessionCheckPending && (showReconnect ? (
                <>
                  {!embedded && (
                  <button
                    type="button"
                    className="mkt-wizard-primary"
                    disabled={busy || status?.enabled === false || connectBlocked}
                    onClick={startConnect}
                  >
                    {busy ? i18n.t('growth:setup.opening') : w('reconnectWithGoogle')}
                  </button>
                  )}
                  {!!mappedAuthIds.length && (
                    <button
                      type="button"
                      className="mkt-wizard-quiet"
                      disabled={busy}
                      onClick={() => loadExistingMappings()}
                    >
                      {w('reviewExisting', { count: reviewableMappingCount })}
                    </button>
                  )}
                  <p className="mkt-wizard-meta">
                    {busy ? w('finishInPopup') : w('sameOrDifferent')}
                  </p>
                </>
              ) : (
                <>
                  {mappedAuthIds.length > 1 ? (
                    <fieldset className="mkt-wizard-account-groups">
                      <legend>{w('reviewByAccount')}</legend>
                      {mappedAuthIds.map((mappedAuthId, index) => {
                        const count = existingForKind.filter(
                          resource =>
                            (resource.authId || resource.linkId) === mappedAuthId,
                        ).length;
                        return (
                          <button
                            key={mappedAuthId}
                            type="button"
                            className="mkt-wizard-quiet"
                            disabled={busy}
                            onClick={() => loadExistingMappings(mappedAuthId)}
                          >
                            {w('accountMappings', { account: index + 1, count })}
                          </button>
                        );
                      })}
                    </fieldset>
                  ) : null}
                  {!!mappedAuthIds.length && (
                    <button
                      type="button"
                      className="mkt-wizard-primary"
                      disabled={busy}
                      onClick={() => loadExistingMappings()}
                    >
                      {mappedAuthIds.length > 1
                        ? w('reviewFirst')
                        : w('reviewMappings', { count: reviewableMappingCount })}
                    </button>
                  )}
                  {kind === 'ads' && !!adsCampaigns.length && (
                    <button
                      type="button"
                      className="mkt-wizard-quiet"
                      disabled={busy}
                      onClick={() => setStep('utm')}
                    >
                      {w('editUtmMap', { count: adsCampaigns.length })}
                    </button>
                  )}
                  {liveAuth && (
                    <button
                      type="button"
                      className="mkt-wizard-quiet"
                      disabled={busy}
                      onClick={() => void loadDiscovery(liveAuth.authId, true)}
                    >
                      {busy ? w('loadingPropertiesShort') : w('chooseGoogleProperties')}
                    </button>
                  )}
                  {liveAuths.length > 1 ? (
                    <div className="mkt-wizard-account-groups" role="group" aria-label={w('chooseAccount')}>
                      {liveAuths.slice(1).map((candidate, index) => (
                        <button
                          key={candidate.authId}
                          type="button"
                          className="mkt-wizard-quiet"
                          disabled={busy}
                          onClick={() => void loadDiscovery(candidate.authId, true)}
                        >
                          {w('chooseFromAccount', { account: index + 2 })}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  {(!embedded || existingForKind.length || liveAuth) && (
                  <button
                    type="button"
                    className={
                      existingForKind.length || liveAuth
                        ? 'mkt-wizard-quiet'
                        : 'mkt-wizard-primary'
                    }
                    disabled={busy || status?.enabled === false || connectBlocked}
                    onClick={startConnect}
                  >
                    {existingForKind.length || liveAuth
                      ? i18n.t('growth:setup.differentAccount')
                      : i18n.t('growth:setup.connectGoogle')}
                  </button>
                  )}
                  {liveSession ? <p className="mkt-wizard-meta">{liveSession}</p> : null}
                </>
              ))}
            </div>
          )}

          {step === 'discover' && (
            <div className="mkt-wizard-step">
              {discoveryWarnings.length ? (
                <ul className="mkt-wizard-warnings" role="status">
                  {discoveryWarnings.map(warning => (
                    <li key={`${warning.kind}:${warning.accountId || ''}:${warning.status}`}>
                      {i18n.t(`growth:connect.warning_${warning.status}`, {
                        product: i18n.t(PRODUCT_LABEL_KEY[warning.kind] || warning.kind),
                        account: warning.accountId || '',
                        defaultValue: i18n.t('growth:connect.warning_error', {
                          product: i18n.t(PRODUCT_LABEL_KEY[warning.kind] || warning.kind),
                        }),
                      })}
                    </li>
                  ))}
                </ul>
              ) : null}
              {notGranted.length ? (
                <p className="mkt-wizard-error" role="alert">
                  {i18n.t('growth:connect.notGranted', {
                    count: notGranted.length,
                    products: notGranted
                      .map(product => i18n.t(PRODUCT_LABEL_KEY[product] || product))
                      .join(', '),
                  })}
                </p>
              ) : null}
              {accountEmail ? (
                <p className="mkt-wizard-meta mkt-wizard-account" role="status">
                  {i18n.t('growth:connect.signedInAs', { email: accountEmail })}{' '}
                  <button
                    type="button"
                    className="mkt-wizard-quiet"
                    disabled={busy || connectBlocked}
                    onClick={startConnect}
                  >
                    {i18n.t('growth:connect.notThisAccount')}
                  </button>
                </p>
              ) : null}
              <div className="mkt-wizard-meta">{w('selectIntro')}</div>
              {!kindDiscovered.length && (
                <>
                  <p className="mkt-wizard-meta">{w('noPropertiesForAccount')}</p>
                  <div className="mkt-wizard-row">
                    {liveAuth && (
                      <button
                        type="button"
                        className="mkt-wizard-quiet"
                        disabled={busy}
                        onClick={() => void loadDiscovery(liveAuth.authId, true)}
                      >
                        {busy ? w('retrying') : w('tryAgain')}
                      </button>
                    )}
                    <button
                      type="button"
                      className="mkt-wizard-primary"
                      disabled={busy || status?.enabled === false || connectBlocked}
                      onClick={startConnect}
                    >
                      {w('connectDifferent')}
                    </button>
                  </div>
                </>
              )}
              {kindDiscovered.length ? (
                <DiscoveryPicker
                  items={kindDiscovered}
                  selected={selected}
                  onToggle={externalId =>
                    setSelected(current => ({ ...current, [externalId]: !current[externalId] }))
                  }
                  orphanIds={
                    new Set(
                      kindDiscovered
                        .filter(
                          d =>
                            !(d.suggestedRestaurantIds || []).length &&
                            !(mapping[d.externalId] || []).length,
                        )
                        .map(d => d.externalId),
                    )
                  }
                  viewerIsStaff={viewerIsStaff}
                  discoveredAt={discoveredAt}
                  busy={busy}
                  onRefresh={authId ? () => void loadDiscovery(authId, true) : undefined}
                  customerName={
                    // aleno's placeholder group ("Default") names no customer:
                    // the restaurant's own name says where "here" is.
                    customerLabel(status?.customerName) ??
                      outlets.find(o => o.id === restaurantId)?.name ?? null
                  }
                  onConnect={credentialId ? openConnect : undefined}
                />
              ) : null}
              <div className="mkt-wizard-row">
                <button
                  type="button"
                  className="mkt-wizard-quiet"
                  onClick={() => setStep('start')}
                >
                  {w('back')}
                </button>
                <button
                  type="button"
                  className="mkt-wizard-primary"
                  disabled={!selectedList.length}
                  onClick={() => setStep('map')}
                >
                  {w('nextMap')}
                </button>
              </div>
            </div>
          )}

          {step === 'map' && (
            <div className="mkt-wizard-step">
              <p className="mkt-wizard-meta">{w('mapIntro')}</p>
              <label className="mkt-wizard-name">
                <span>{i18n.t('growth:connections.renameLabel')}</span>
                <input
                  type="text"
                  value={connectionNameDraft}
                  maxLength={60}
                  placeholder={i18n.t('growth:connections.renamePlaceholder')}
                  onChange={event => setConnectionNameDraft(event.target.value)}
                />
                <small>{i18n.t('growth:connections.renameHint')}</small>
              </label>
              {mappedPreviewIds.length > 0 && (
                <div className="mkt-wizard-summary">
                  {[
                    w('mappedCount', { count: mappedPreviewIds.length }),
                    mappedPreviewNew.length
                      ? w('notInScopeCount', { count: mappedPreviewNew.length })
                      : w('allInScope'),
                    outlets.length > 50 ? w('availableToAdd', { count: outlets.length }) : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              )}
              <div className="mkt-wizard-filters" role="tablist" aria-label={w('filterAria')}>
                {(
                  [
                    ['selected', w('filterMapped', { count: mappedPreviewIds.length })],
                    ['inScope', w('filterInScope', { count: mappedPreviewIds.length - mappedPreviewNew.length })],
                    ['outsideScope', w('filterOutside', { count: mappedPreviewNew.length })],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    className={`growth-tab${listFilter === id ? ' on' : ''}`}
                    aria-selected={listFilter === id}
                    onClick={() => setListFilter(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {selectedList.map(d => {
                const selectedIds = mapping[d.externalId] || [];
                const selectedSet = new Set(selectedIds);
                const outletQuery = outletQueryByResource[d.externalId] || '';
                const counts = mappingCounts(selectedIds, scopedSet);
                const reviewed = reviewMappedOutlets({
                  outlets,
                  selectedIds,
                  scopedIds: scopedSet,
                  filter: listFilter,
                  query: outletQuery,
                });
                const addHits = searchOutletsToAdd({
                  outlets,
                  selectedIds: selectedSet,
                  query: outletQuery,
                });
                const nameHints = suggestedOutletsToAdd({
                  outlets,
                  suggestedIds: d.suggestedRestaurantIds || [],
                  selectedIds: selectedSet,
                });
                const scopedMissing = scopedRestaurantIds.filter(id => !selectedSet.has(id));
                const searching = outletQuery.trim().length > 0;
                return (
                  <div key={d.externalId} className="mkt-wizard-map-block">
                    <div className="mkt-wizard-map-head">
                      <div>
                        <div className="mkt-wizard-res-name">{d.displayName}</div>
                        <div className="mkt-wizard-meta">
                          {w('resourceCounts', {
                            mapped: counts.selected,
                            inScope: counts.inScope,
                            outside: counts.outsideScope,
                          })}
                        </div>
                      </div>
                      <div className="mkt-wizard-map-actions">
                        {scopedMissing.length > 0 && (
                          <button
                            type="button"
                            className="mkt-wizard-quiet"
                            onClick={() => addOutlets(d.externalId, scopedMissing)}
                          >
                            {w('addCurrentScope', { count: scopedMissing.length })}
                          </button>
                        )}
                        {selectedIds.length > 0 && (
                          <button
                            type="button"
                            className="mkt-wizard-quiet"
                            onClick={() => setResourceMapping(d.externalId, [])}
                          >
                            {w('clearMapped')}
                          </button>
                        )}
                      </div>
                    </div>
                    <input
                      className="mkt-wizard-search"
                      value={outletQuery}
                      onChange={e =>
                        setOutletQueryByResource(q => ({
                          ...q,
                          [d.externalId]: e.target.value,
                        }))
                      }
                      placeholder={w('searchRestaurants')}
                      aria-label={w('searchRestaurantsFor', { property: d.displayName })}
                    />
                    {searching && addHits.total > 0 && (
                      <div className="mkt-wizard-add is-search">
                        <div className="mkt-wizard-map-head">
                          <div className="mkt-wizard-res-name">
                            {w('addFromSearch', { count: addHits.total })}
                          </div>
                          <button
                            type="button"
                            className="mkt-wizard-quiet"
                            onClick={() =>
                              addOutlets(
                                d.externalId,
                                searchOutletsToAdd({
                                  outlets,
                                  selectedIds: selectedSet,
                                  query: outletQuery,
                                  limit: Number.MAX_SAFE_INTEGER,
                                }).rows.map(o => o.id),
                              )
                            }
                          >
                            {w('addAllMatches', { count: addHits.total })}
                          </button>
                        </div>
                        <ul className="mkt-wizard-outlet-list">
                          {addHits.rows.map(o => (
                            <li key={o.id} className="mkt-wizard-outlet is-add">
                              <button
                                type="button"
                                className="mkt-wizard-quiet"
                                onClick={() => addOutlets(d.externalId, [o.id])}
                              >
                                {w('add')}
                              </button>
                              <span>
                                {o.name || o.id}
                                {o.city ? ` · ${o.city}` : ''}
                                {scopedSet.has(o.id) ? (
                                  <em className="mkt-wizard-in-scope"> · {w('inThisScope')}</em>
                                ) : null}
                              </span>
                            </li>
                          ))}
                        </ul>
                        {addHits.truncated ? (
                          <p className="mkt-wizard-meta">
                            {w('showingMatches', { shown: addHits.rows.length, count: addHits.total })}
                          </p>
                        ) : null}
                      </div>
                    )}
                    {searching && addHits.total === 0 && (
                      <p className="mkt-wizard-empty" role="status">
                        {w('noUnmappedMatch', { query: outletQuery.trim() })}
                      </p>
                    )}
                    <ul className="mkt-wizard-outlet-list">
                      {reviewed.map(o => {
                        const inScope = scopedSet.has(o.id);
                        return (
                          <li key={o.id} className="mkt-wizard-outlet is-mapped">
                            <span>
                              {o.name || o.id}
                              {o.city ? ` · ${o.city}` : ''}
                              <em className="mkt-wizard-in-scope">
                                {' · '}
                                {inScope ? w('inThisScope') : w('notInThisScope')}
                              </em>
                            </span>
                            <button
                              type="button"
                              className="mkt-wizard-quiet"
                              onClick={() => removeOutlet(d.externalId, o.id)}
                            >
                              {w('remove')}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                    {!reviewed.length && !searching && (
                      <p className="mkt-wizard-empty">
                        {selectedIds.length
                          ? listFilter === 'outsideScope'
                            ? w('emptyOutside')
                            : listFilter === 'inScope'
                              ? w('emptyInScope')
                              : w('emptySearch')
                          : w('emptyNothing')}
                      </p>
                    )}
                    {!searching && nameHints.length > 0 && (
                      <div className="mkt-wizard-add is-hints">
                        <div className="mkt-wizard-res-name">{w('suggested')}</div>
                        <p className="mkt-wizard-meta">{w('suggestedHint')}</p>
                        <ul className="mkt-wizard-outlet-list">
                          {nameHints.map(o => (
                            <li key={o.id} className="mkt-wizard-outlet is-add">
                              <button
                                type="button"
                                className="mkt-wizard-quiet"
                                onClick={() => addOutlets(d.externalId, [o.id])}
                              >
                                {w('add')}
                              </button>
                              <span>
                                {o.name || o.id}
                                {o.city ? ` · ${o.city}` : ''}
                                {scopedSet.has(o.id) ? (
                                  <em className="mkt-wizard-in-scope"> · {w('inThisScope')}</em>
                                ) : null}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                );
              })}
              {kind === 'ga4' && (
                <div className="mkt-wizard-events">
                  <div className="mkt-wizard-events-head">
                    <span className="mkt-wizard-res-name">{w('eventsTitle')}</span>
                    <Infotip label={w('eventsTitle')} text={w('eventsHelp')} />
                  </div>
                  {eventsBusy && (
                    <p className="mkt-wizard-meta">{w('eventsChecking')}</p>
                  )}
                  {detectedEvents && !detectedEvents.length && !eventsBusy && (
                    <div className="mkt-wizard-event-warn">
                      <strong>{w('eventsNoneTitle')}</strong> {w('eventsNoneBody')}{' '}
                      <a href={GTM_GUIDE} target="_blank" rel="noreferrer">
                        {w('eventsHowTo')}
                      </a>
                    </div>
                  )}
                  {detectedEvents && detectedEvents.length > 0 && missingEvents.length > 0 && (
                    <div className="mkt-wizard-event-warn">
                      <strong>
                        {w('eventsMissingTitle', {
                          count: missingEvents.length,
                          events: missingEvents.map(k => eventLabel(k)).join(', '),
                        })}
                      </strong>{' '}
                      {w('eventsMissingBody')}{' '}
                      <a href={GTM_GUIDE} target="_blank" rel="noreferrer">
                        {w('eventsHowTo')}
                      </a>
                    </div>
                  )}
                  {EVENT_KEYS.map(k => (
                    <EventNameField
                      key={k}
                      label={eventLabel(k)}
                      value={eventConfig[k]}
                      events={detectedEvents}
                      custom={customEvent[k]}
                      onChange={next => setEventConfig(c => ({ ...c, [k]: next }))}
                      onCustomChange={next =>
                        setCustomEvent(c => ({ ...c, [k]: next }))
                      }
                    />
                  ))}
                </div>
              )}
              {!embedded ? (
                <div className="mkt-wizard-row">
                  <button
                    type="button"
                    className="mkt-wizard-quiet"
                    onClick={() => setStep(mode === 'manage' ? 'start' : 'discover')}
                  >
                    {w('back')}
                  </button>
                  <button
                    type="button"
                    className="mkt-wizard-primary"
                    disabled={busy}
                    onClick={() => void save()}
                  >
                    {busy ? w('saving') : w('saveMappings')}
                  </button>
                </div>
              ) : null}
            </div>
          )}

          {step === 'scope' && (
            <div className="mkt-wizard-step">
              <p>{w('scopeQuestion', { count: pendingMappedIds.filter(id => !scopedSet.has(id)).length })}</p>
              <ul className="mkt-wizard-scope-list">
                {pendingMappedIds
                  .filter(id => !scopedSet.has(id))
                  .map(id => {
                    const outlet = outlets.find(o => o.id === id);
                    return (
                      <li key={id}>{outlet?.name || id}{outlet?.city ? ` · ${outlet.city}` : ''}</li>
                    );
                  })}
              </ul>
              <p className="mkt-wizard-meta">{w('scopeHint')}</p>
              <div className="mkt-wizard-row">
                <button
                  type="button"
                  className="mkt-wizard-quiet"
                  onClick={() => continueAfterScope(false)}
                >
                  {w('keepScope')}
                </button>
                <button
                  type="button"
                  className="mkt-wizard-primary"
                  onClick={() => continueAfterScope(true)}
                >
                  {w('useMappedScope')}
                </button>
              </div>
            </div>
          )}

          {step === 'utm' && (
            <div className="mkt-wizard-step">
              <p>
                <Trans i18nKey="growth:wizard.utmIntro" components={{ code: <code /> }} />
              </p>
              {!adsCampaigns.length && <div className="mkt-wizard-meta">{w('utmNoCatalog')}</div>}
              {adsCampaigns.map(c => {
                const key = c.campaignId || c.campaignName;
                return (
                  <label key={key} className="mkt-wizard-event">
                    <span>{c.campaignName}</span>
                    <input
                      value={utmMap[c.campaignId] || utmMap[c.campaignName] || ''}
                      placeholder={c.campaignName}
                      onChange={e => {
                        const v = e.target.value;
                        setUtmMap(m => {
                          const next = { ...m };
                          if (c.campaignId) next[c.campaignId] = v;
                          if (c.campaignName) next[c.campaignName] = v;
                          return next;
                        });
                      }}
                    />
                  </label>
                );
              })}
              <div className="mkt-wizard-row">
                <button
                  type="button"
                  className="mkt-wizard-quiet"
                  onClick={() => {
                    setStep('done');
                    onConnected();
                  }}
                >
                  {w('skip')}
                </button>
                <button
                  type="button"
                  className="mkt-wizard-primary"
                  disabled={busy || !adsCampaigns.length}
                  onClick={saveUtm}
                >
                  {busy ? w('saving') : w('saveUtm')}
                </button>
              </div>
            </div>
          )}

          {step === 'done' && (
            <div className="mkt-wizard-step">
              <p role="status">
                {syncNotice || w('connectedDone')}
              </p>
              <button type="button" className="mkt-wizard-primary" onClick={onClose}>
                {w('done')}
              </button>
            </div>
          )}
        </div>
        {connectSheet ? (
          <ConnectPropertySheet
            restaurantId={restaurantId}
            request={connectSheet}
            restaurants={restaurantOptions}
            onClose={() => setConnectSheet(null)}
            onConnected={next => {
              setConnectSheet(null);
              setConnectToast(next);
              void refreshDiscovered();
            }}
          />
        ) : null}
        {connectToast ? (
          <ConnectToast
            restaurantId={restaurantId}
            toast={connectToast}
            onDismiss={() => setConnectToast(null)}
            onUndone={() => void refreshDiscovered()}
          />
        ) : null}
        {embedded && step === 'map' ? (
          <div className="mkt-wizard-row is-docked">
            <button
              type="button"
              className="mkt-wizard-quiet"
              onClick={() => setStep(mode === 'manage' ? 'start' : 'discover')}
            >
              {w('back')}
            </button>
            <button
              type="button"
              className="mkt-wizard-primary"
              disabled={busy}
              onClick={() => void save()}
            >
              {busy ? w('saving') : w('saveMappings')}
            </button>
          </div>
        ) : null}
    </>
  );

  if (embedded) {
    return (
      <div className="mkt-wizard is-embedded" data-hj-suppress>
        {body}
      </div>
    );
  }

  return (
    <>
      <div className="scrim" onClick={onClose} role="presentation" />
      <div
        ref={drawerRef}
        className="drawer mkt-wizard"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        data-ftue="marketing-wizard"
        data-hj-suppress
      >
        {body}
      </div>
    </>
  );
}

/**
 * One funnel step's event name. Once the property has told us what it records,
 * this becomes a list of real events with their volumes; typing a name by hand
 * stays available, because an event the widget has not fired yet is still a
 * legitimate answer.
 */
function EventNameField({
  label,
  value,
  events,
  custom,
  onChange,
  onCustomChange,
}: {
  label: string;
  value: string;
  events: DetectedEvent[] | null;
  custom: boolean;
  onChange: (next: string) => void;
  onCustomChange: (next: boolean) => void;
}) {
  const detected = events ?? [];
  const known = detected.some(e => e.name === value);
  const asList = detected.length > 0 && !custom;

  if (!asList) {
    return (
      <label className="mkt-wizard-event">
        <span>{label}</span>
        <span className="mkt-wizard-event-control">
          <input value={value} onChange={e => onChange(e.target.value)} />
          {detected.length > 0 && (
            <button
              type="button"
              className="mkt-wizard-quiet"
              onClick={() => onCustomChange(false)}
            >
              {w('chooseRecorded')}
            </button>
          )}
        </span>
      </label>
    );
  }

  return (
    <label className="mkt-wizard-event">
      <span>{label}</span>
      <span className="mkt-wizard-event-control">
        <select
          value={known ? value : UNRECORDED_EVENT_OPTION}
          onChange={e => {
            if (e.target.value === CUSTOM_EVENT_OPTION) {
              onCustomChange(true);
              return;
            }
            if (e.target.value === UNRECORDED_EVENT_OPTION) return;
            onChange(e.target.value);
          }}
        >
          {!known && (
            <option value={UNRECORDED_EVENT_OPTION}>
              {value ? w('neverRecorded', { name: value }) : w('chooseEvent')}
            </option>
          )}
          {detected.map(e => (
            <option key={e.name} value={e.name}>
              {w('eventCount', { name: e.name, value: fmtInt(e.count) })}
            </option>
          ))}
          <option value={CUSTOM_EVENT_OPTION}>{w('enterManually')}</option>
        </select>
      </span>
    </label>
  );
}
